import {
  Button,
  ButtonGroup,
  Card,
  Input,
  Label,
  TextAreaField,
} from "@aws-amplify/ui-react"
import { EuDataActRawDataSchema } from "@strtmn/sds/schemas"
import type {
  AddressObjectDto,
  EuDataActRawDataDto,
  MachineSupaDto,
  OrganizationDto,
  UserDataDto,
} from "@strtmn/sds/types"
import {
  dateOnly,
  // formatUnicorn,
  getErrorMessage,
  // parseTimestamp,
} from "@strtmn/strtmn"
import { I18n } from "aws-amplify/utils"
import { useContext, useEffect, useState, type PropsWithChildren } from "react"
import {
  Controller,
  FormProvider,
  get,
  useForm,
  type FieldErrors,
  type SubmitHandler,
} from "react-hook-form"
import {
  // FaDownload,
  FaRegCalendarDays,
  FaRegCalendarXmark,
} from "react-icons/fa6"
import { useNavigate, useParams } from "react-router-dom"
import DatePicker from "react-tailwindcss-datepicker"
import { requestRawData } from "../../api/EuDataActApi"
import { CancelButton, ErrorBanner, LoadingErrorState } from "../../components"
import VehiclePageLayout from "../../components/VehiclePageLayout"
import { isProd } from "../../config"
import { UserDataContext, UserDataContextHelper } from "../../contexts"
import { useActiveOrgId } from "../../hooks"
import { useYupValidationResolver } from "../../hooks/useYupValidationResolver"
import { getLinkToVehicleEuDataAct } from "../../LinkUtils"
import { getCurrentUserId } from "../../Utils.AmplifyAuth"

interface PageWrapperProps extends PropsWithChildren {
  vin: string
}
function PageWrapper({ vin, children }: PageWrapperProps) {
  return (
    <VehiclePageLayout vin={vin}>
      <Card>
        <div className="section-heading mb-6">
          {I18n.get("Anfrage zur Bereitstellung von Rohdaten")}
        </div>
        {children}
      </Card>
    </VehiclePageLayout>
  )
}

/** @todo: extract */
interface IResponseMessage {
  type: "success" | "load-error" | "submit-error"
  message: string
}

export default function RawDataDownload() {
  const navigate = useNavigate()
  const userPrefLang = "de" // useUserPrefLang()
  const params = useParams<{
    vin: string | undefined
    date: string | undefined
  }>()
  const { userData } = useContext(UserDataContext)
  const activeOrgId = useActiveOrgId()

  const [currentUserId, setCurrentUserId] = useState<string | undefined>(
    undefined
  )
  const [loading, setLoading] = useState(true)
  const [responseMessage, setResponseMessage] =
    useState<IResponseMessage | null>(null)
  const [isFormSubmitting, setIsFormSubmitting] = useState(false)
  const now = new Date()

  const vin = params.vin!
  let machineFromUserCtx = undefined
  if (activeOrgId && userData) {
    const org = UserDataContextHelper.findOrg(activeOrgId, userData)
    const machinesFromUserCtx = org.Machines
    !isProd &&
      console.debug(
        `${machinesFromUserCtx.length} machines from userData:`,
        machinesFromUserCtx
      )
    machineFromUserCtx = machinesFromUserCtx.find(
      (supa) => supa.serial_number === vin
    )
  }

  const loadedUserData: UserDataDto = userData!
  const allAddresses: { orgName: string; address: AddressObjectDto }[] = []
  loadedUserData.Organizations.forEach((o: OrganizationDto) => {
    const a: AddressObjectDto =
      typeof o.address === "string"
        ? (JSON.parse(o.address) as AddressObjectDto)
        : o.address
    allAddresses.push({ orgName: o.name, address: a })
  })
  const hasExactlyOneAddress = allAddresses.length === 1

  const userAddressValue = allAddresses
    .flatMap(({ orgName, address }) => {
      const lines: string[] = []
      if (orgName) {
        lines.push(orgName)
      }
      if (address) {
        // If street is there, add a line. Number may be empty.
        if (address.street) {
          lines.push(`${address.street} ${address.number}`.trim())
        }
        if (address.country || address.zip || address.city) {
          lines.push(`${address.country} ${address.zip} ${address.city}`.trim())
        }
      }
      if (lines.length > 0) {
        return lines.join("\n")
      }
      return []
    })
    .join("\n\n")

  const userNameList: string[] = []
  if (hasExactlyOneAddress && allAddresses[0]?.orgName) {
    userNameList.push(allAddresses[0]?.orgName.trim())
    userNameList.push("/")
  }
  loadedUserData.first_name &&
    userNameList.push(loadedUserData.first_name.trim())
  loadedUserData.last_name && userNameList.push(loadedUserData.last_name.trim())
  const userName = userNameList.join(" ")

  const resolver = useYupValidationResolver(EuDataActRawDataSchema)
  const methods = useForm<EuDataActRawDataDto>({
    // values: {
    //   userId: currentUserId
    // },
    defaultValues: {
      requestingName: userName,
    },
    resolver,
  })
  const {
    control,
    formState: { errors },
    handleSubmit,
    register,
    setError,
    setValue,
    watch,
  } = methods

  useEffect(() => {
    console.debug("> raw-data.useEffect (load data)")

    const fetchAllData = async () => {
      !isProd && console.debug("> data-sharing.fetchAllData")
      setLoading(true)
      setCurrentUserId(undefined)
      setResponseMessage(null)
      try {
        const myCurrentUserId = await getCurrentUserId()
        setCurrentUserId(myCurrentUserId)
        setValue("userId", myCurrentUserId)
      } catch (err) {
        console.error("Error:", err)
        setResponseMessage({
          type: "load-error",
          message: getErrorMessage(err),
        })
      } finally {
        setLoading(false)
      }
    }

    fetchAllData()
  }, [])

  if (loading || responseMessage?.type === "load-error") {
    return (
      <PageWrapper vin={vin}>
        <LoadingErrorState
          isLoading={loading}
          loadingMessage={I18n.get("Lade Maschinendaten, bitte warten...")}
          isError={!!(responseMessage?.type === "load-error")}
          errorMessage={`${I18n.get("Fehler")}: ${I18n.get(responseMessage?.message!)}`}
        />
      </PageWrapper>
    )
  }
  if (!machineFromUserCtx) {
    return (
      <PageWrapper vin={vin}>
        <ErrorBanner
          error={`${I18n.get("Fehler")}: ${I18n.get("Keine Maschinendaten geladen.")}`}
        />
      </PageWrapper>
    )
  }

  const loadedMachine: MachineSupaDto = machineFromUserCtx
  !isProd && console.log("loadedMachine =", loadedMachine)

  const onSubmit: SubmitHandler<any> = async (data: EuDataActRawDataDto) => {
    !isProd && console.debug("> onSubmit")
    setIsFormSubmitting(true)
    !isProd && console.log("onSubmit: data =", data)
    setResponseMessage(null)
    try {
      await requestRawData(data)
      navigate(
        getLinkToVehicleEuDataAct({ vin: loadedMachine.serial_number }),
        {
          replace: true,
          state: {
            successMessage: I18n.get(
              "Die Anfrage zur Bereitstellung von Rohdaten wurde versendet."
            ),
          },
        }
      )
    } catch (err) {
      console.error("Could not submit the form:", getErrorMessage(err))
      setResponseMessage({
        type: "submit-error",
        message: getErrorMessage(err),
      })
    } finally {
      setIsFormSubmitting(false)
    }
  }

  const onError = (_errors: FieldErrors) => {
    !isProd && console.log("Validation failed: onError", _errors)
    setError("root.clientError", {
      type: "validate",
      message: I18n.get(
        "Das Formular enthält ungültige Eingaben. Beachten Sie die Fehlermeldungen."
      ),
    })
  }

  // const onDatePickerChange = (newValue: DateValueType) => {
  //   !isProd && console.debug("onDatePickerChange:", newValue)
  //   if (!newValue) {
  //     return
  //   }
  //   newValue.startDate?.setHours(0, 0, 0, 0)
  //   newValue.endDate?.setHours(23, 59, 59, 0)
  //   setDatePickerValue(newValue)
  // }

  const requestingName = watch("requestingName")
  const requestedStartDateValue = watch("requestedStartDate")
  const requestedEndDateValue = watch("requestedEndDate")

  // Smol helper, as DatePicker values may be `Date` or `string`
  const toIsoString = (val: Date | string | null | undefined): string => {
    if (!val) {
      return ""
    }
    if (val instanceof Date) {
      return val.toISOString()
    }
    return val // already an ISO string from the picker
  }

  return (
    <PageWrapper vin={vin}>
      <FormProvider {...methods}>
        <form
          onSubmit={handleSubmit(onSubmit, onError)}
          className="max-w-screen-md"
        >
          <div className="text-center">{I18n.get("von")}</div>
          <div>
            1. {requestingName}
            {/* [{I18n.get("Name des Nutzers")}] */}
          </div>
          <div className="text-right">
            - {I18n.get('nachfolgend auch "Nutzer" genannt')} -
          </div>
          <div className="text-center">{I18n.get("an")}</div>
          <div>2. B. Strautmann & Söhne GmbH u. Co. KG</div>
          <div className="text-right">
            - {I18n.get('nachfolgend auch "Dateninhaber" genannt')} -
          </div>
          <div>&nbsp;</div>
          <div>
            {/* ggf. Formulierung noch weiter ausschmücken, analog zum DataSharing Formular */}
            {I18n.get(
              "Hiermit verlange ich als Nutzer oder als eine im Namen des Nutzers handelnde Partei, dass der Dateninhaber dem Nutzer ohne Weiteres verfügbare Daten sowie die für die Auslegung und Nutzung dieser Daten erforderlichen Metadaten bereitstellt."
            )}
          </div>
          <div>&nbsp;</div>
          <div>
            <Label>
              {I18n.get("Zeitraum")}:<br />
              <Controller
                name="requestedStartDate"
                control={control}
                rules={{ required: true }}
                render={({ field: { ref, ...field } }) => {
                  // console.log("⚡⚡⚡ field =", field)
                  return (
                    <DatePicker
                      {...field}
                      inputName={field.name}
                      // inputClassName="w-full rounded-md focus:ring-0 font-normal bg-blue-300 placeholder:text-blue-100 text-white dark:bg-blue-900 dark:placeholder:text-blue-100"
                      inputClassName="p-0 text-gray-700 outline-none border-none"
                      // containerClassName="relative mt-8"
                      containerClassName="w-auto relative px-3 py-2 datepicker-container border-2 inline"
                      // toggleClassName="absolute bg-blue-300 rounded-r-lg text-white right-0 h-full px-3 text-gray-400 focus:outline-none disabled:opacity-40 disabled:cursor-not-allowed"
                      toggleClassName="mr-0"
                      i18n={userPrefLang || undefined}
                      displayFormat={I18n.get("DD.MM.YYYY")}
                      onChange={(newValue) => {
                        if (!newValue) {
                          setValue("requestedStartDate", "", {
                            shouldValidate: true,
                          })
                          setValue("requestedEndDate", "", {
                            shouldValidate: true,
                          })
                          return
                        }
                        newValue.startDate?.setHours(0, 0, 0, 0)
                        newValue.endDate?.setHours(23, 59, 59, 0)
                        setValue(
                          "requestedStartDate",
                          toIsoString(newValue.startDate),
                          { shouldValidate: true }
                        )
                        setValue(
                          "requestedEndDate",
                          toIsoString(newValue.endDate),
                          { shouldValidate: true }
                        )
                      }}
                      readOnly={true}
                      required={true}
                      // startFrom={datePickerValue?.startDate}
                      startWeekOn="mon"
                      toggleIcon={(open: boolean) =>
                        open ? (
                          <FaRegCalendarDays className="icon inline" />
                        ) : (
                          <FaRegCalendarXmark className="icon inline" />
                        )
                      }
                      value={{
                        startDate: requestedStartDateValue
                          ? new Date(requestedStartDateValue)
                          : null,
                        endDate: requestedEndDateValue
                          ? new Date(requestedEndDateValue)
                          : null,
                      }}
                    />
                  )
                }}
              />
            </Label>
            {get(errors, "requestedStartDate") && (
              <span className="error block">
                {I18n.get(get(errors, "requestedStartDate").message)}
              </span>
            )}
            {get(errors, "requestedEndDate") && (
              <span className="error block">
                {I18n.get(get(errors, "requestedEndDate").message)}
              </span>
            )}
          </div>
          <div>&nbsp;</div>
          <div>
            <Label>{I18n.get("Maschinennummer")}</Label>
            <br />
            {vin}
            <input
              type="hidden"
              {...register("machineSerialNumber")}
              value={vin}
            />
            {errors.machineSerialNumber && (
              <span className="error">
                {I18n.get(errors.machineSerialNumber.message!)}
              </span>
            )}
          </div>
          <div>&nbsp;</div>
          <div>
            <Label>
              {I18n.get(
                "Ihr Name als Nutzer oder als eine im Namen des Nutzers handelnde Partei"
              )}
              <Input
                {...register("requestingName")}
                size="small"
                placeholder={I18n.get(
                  "Ihr Name als Nutzer oder als eine im Namen des Nutzers handelnde Partei"
                )}
                defaultValue={userName}
              />
            </Label>
            {get(errors, "requestingName") && (
              <span className="error">
                {I18n.get(get(errors, "requestingName").message)}
              </span>
            )}
          </div>
          <div>
            <Label>
              {I18n.get("Name und Anschrift des Nutzers")}
              <TextAreaField
                {...register("userNameAndAddress")}
                size="small"
                resize="vertical"
                rows={5}
                label={I18n.get("Name und Anschrift des Nutzers")}
                labelHidden={true}
                defaultValue={userAddressValue}
              />
            </Label>
            {get(errors, "userNameAndAddress") && (
              <span className="error">
                {I18n.get(get(errors, "userNameAndAddress").message)}
              </span>
            )}
          </div>
          <div>&nbsp;</div>
          <div>
            <Label>{I18n.get("Datum")}</Label>
            <br />
            {now.toLocaleDateString(userPrefLang, { dateStyle: "long" })}
            <input
              type="hidden"
              {...register("dateOfRequest")}
              value={dateOnly(now.toISOString())}
            />
            {get(errors, "dateOfRequest") && (
              <span className="error">
                {I18n.get(get(errors, "dateOfRequest").message)}
              </span>
            )}
          </div>

          <input type="hidden" {...register("userId")} value={currentUserId} />
          {get(errors, "userId") && (
            <span className="error">
              {I18n.get(get(errors, "userId").message)}
            </span>
          )}

          {get(errors, "root.clientError") && (
            <ErrorBanner
              error={I18n.get(get(errors, "root.clientError").message)}
            />
          )}

          {responseMessage?.type === "submit-error" && (
            <ErrorBanner error={I18n.get(responseMessage.message)} />
          )}

          <ButtonGroup className="buttons !mt-4" size="small">
            <Button
              type="submit"
              variation="primary"
              isDisabled={isFormSubmitting}
              isLoading={isFormSubmitting}
              loadingText={I18n.get("Anfrage wird gesendet...")}
            >
              {I18n.get("Anfrage senden")}
            </Button>

            <CancelButton
              href={getLinkToVehicleEuDataAct({
                vin: loadedMachine.serial_number,
              })}
              title="Abbrechen"
              isDisabled={isFormSubmitting}
            />
          </ButtonGroup>
        </form>
      </FormProvider>
    </PageWrapper>
  )
}
